import * as Tone from "tone";
import type { DrumId } from "../constants/drums";

export interface DrumVoice {
  trigger(time?: number): void;
  setRelease(seconds: number): void;
  connect(dest: Tone.ToneAudioNode): void;
  dispose(): void;
}

// For drum voices (sustain: 0) the perceptually dominant tail is the decay
// phase, not the release. "setRelease" therefore scales the envelope decay
// (and keeps the release proportional) so the dial actually audibly changes
// the sound.
function applyDrumDecay(
  env: { decay: Tone.Unit.Time; release: Tone.Unit.Time },
  seconds: number,
): void {
  env.decay = seconds;
  env.release = seconds * 0.4;
}

function createKick(): DrumVoice {
  const synth = new Tone.MembraneSynth({
    pitchDecay: 0.04,
    octaves: 6,
    envelope: { attack: 0.001, decay: 0.35, sustain: 0, release: 0.1 },
  });
  return {
    trigger: (time) => synth.triggerAttackRelease("C2", "8n", time),
    setRelease: (seconds) => applyDrumDecay(synth.envelope, seconds),
    connect: (d) => {
      synth.connect(d);
    },
    dispose: () => synth.dispose(),
  };
}

function createSnare(): DrumVoice {
  const synth = new Tone.NoiseSynth({
    noise: { type: "white" },
    envelope: { attack: 0.001, decay: 0.18, sustain: 0, release: 0.05 },
  });
  return {
    trigger: (time) => synth.triggerAttackRelease("16n", time),
    setRelease: (seconds) => applyDrumDecay(synth.envelope, seconds),
    connect: (d) => {
      synth.connect(d);
    },
    dispose: () => synth.dispose(),
  };
}

function createHat(): DrumVoice {
  const synth = new Tone.NoiseSynth({
    noise: { type: "white" },
    envelope: { attack: 0.001, decay: 0.06, sustain: 0, release: 0.02 },
  });
  const hp = new Tone.Filter({ type: "highpass", frequency: 7000 });
  synth.connect(hp);
  return {
    trigger: (time) => synth.triggerAttackRelease("32n", time),
    setRelease: (seconds) => applyDrumDecay(synth.envelope, seconds),
    connect: (d) => {
      hp.connect(d);
    },
    dispose: () => {
      synth.dispose();
      hp.dispose();
    },
  };
}

function createClap(): DrumVoice {
  const synth = new Tone.NoiseSynth({
    noise: { type: "pink" },
    envelope: { attack: 0.002, decay: 0.22, sustain: 0, release: 0.08 },
  });
  const bp = new Tone.Filter({ type: "bandpass", frequency: 1500, Q: 1.2 });
  synth.connect(bp);
  return {
    trigger: (time) => synth.triggerAttackRelease("16n", time),
    setRelease: (seconds) => applyDrumDecay(synth.envelope, seconds),
    connect: (d) => {
      bp.connect(d);
    },
    dispose: () => {
      synth.dispose();
      bp.dispose();
    },
  };
}

function createTom(): DrumVoice {
  const synth = new Tone.MembraneSynth({
    pitchDecay: 0.05,
    octaves: 3,
    envelope: { attack: 0.001, decay: 0.3, sustain: 0, release: 0.1 },
  });
  return {
    trigger: (time) => synth.triggerAttackRelease("A2", "8n", time),
    setRelease: (seconds) => applyDrumDecay(synth.envelope, seconds),
    connect: (d) => {
      synth.connect(d);
    },
    dispose: () => synth.dispose(),
  };
}

const BUILDERS: Record<DrumId, () => DrumVoice> = {
  kick: createKick,
  snare: createSnare,
  hat: createHat,
  clap: createClap,
  tom: createTom,
};

export function createDrumVoice(id: DrumId): DrumVoice {
  return BUILDERS[id]();
}
